/**
 * Admin panel — i18n entry point.
 *
 * **Co tutaj jest:** stringi panelu administracyjnego (AdminTopBar, dropdowny,
 * LayoutAndWindowsPanel, AlignmentChecker, toasty z contextu admina, ...). NIE
 * używa `lib/i18n/locales/*.ts` (te są visitor-facing).
 *
 * **Wybór języka:** backend `IAPI_ssoCurrentSession` zwraca `admin_locale`
 * czytane z `$_SESSION['sa_lang']` w sesji Imperial (shared SoT z legacy panelem
 * IADMIN). Lista wspieranych języków pochodzi z `imp_language WHERE for='2'`
 * (admin languages) — aktualnie pl + en.
 *
 * @example
 *   import { getAdminMessages } from "@/lib/i18n/admin";
 *   const t = getAdminMessages("en");
 *   t.topBar.userMenu.logout // -> "Log out"
 *   t.pendingBar.statusLabel(3) // -> "Working… (3)"
 */

import { adminMessagesPl, type AdminMessagesShape } from "./pl";
import { adminMessagesEn } from "./en";

/** Symbole języków zarejestrowanych w backend `imp_language WHERE for='2'`. */
export const ADMIN_LOCALES = ["pl", "en"] as const;
export type AdminLocale = (typeof ADMIN_LOCALES)[number];

/** Default — backend wymusza `cfg()->defaultLangAdminApi` (zwykle "pl"). */
export const DEFAULT_ADMIN_LOCALE: AdminLocale = "pl";

const ALL_ADMIN_MESSAGES: Record<AdminLocale, AdminMessagesShape> = {
  pl: adminMessagesPl,
  en: adminMessagesEn,
};

/**
 * Type guard — sprawdza czy string jest valid AdminLocale.
 * Backend może w przyszłości dodać kolejne języki w `imp_language for='2'`
 * — wtedy aktualizujemy `ADMIN_LOCALES` + dorzucamy nowy plik tłumaczeń.
 */
export function isAdminLocale(value: unknown): value is AdminLocale {
  return typeof value === "string" && (ADMIN_LOCALES as readonly string[]).includes(value);
}

/**
 * Zwraca translations object dla podanego admin locale.
 * Fallback do `DEFAULT_ADMIN_LOCALE` ("pl") gdy nieobsługiwany.
 */
export function getAdminMessages(locale: string): AdminMessagesShape {
  if (isAdminLocale(locale)) {
    return ALL_ADMIN_MESSAGES[locale];
  }
  return ALL_ADMIN_MESSAGES[DEFAULT_ADMIN_LOCALE];
}

export type { AdminMessagesShape };
